import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Formik, useFormik } from "formik";
import { t } from "i18next";

// API
import {
  useMarketingTagsQuery,
  useCreateMarketingTagMutation,
  useUpdateMarketingTagMutation,
  useDeleteMarketingTagMutation,
} from "@/app/marketing-tags/marketing-tags.api";

import {
  billInitialValues,
  billValidationSchema,
} from "@/shared/schemas/settings.schema";

//Components
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import TableListTagsMarketing from "@/shared/components/organisms/TableListTagsMarketing";
import { Page } from "@/shared/components/atoms/Page";
import { useCoursesFeeQuery } from "@/app/courses/courses.api";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { useFeesMutation } from "@/app/fees/fees.api";
import { toasts } from "@/shared/utils/toasts";

export const UserTags: FC<any> = () => {
  const { id } = useParams();

  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const marketingTagsQuery = useMarketingTagsQuery({ branchId: id, type: 2 });
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [createMarketingTag, createMarketingTagResult] =
    useCreateMarketingTagMutation();
  const [updateMarketingTag, updateMarketingTagResult] =
    useUpdateMarketingTagMutation();
  const [deleteMarketingTag, deleteMarketingTagResult] =
    useDeleteMarketingTagMutation();

  const coursesFeeQuery = useCoursesFeeQuery({
    branchId: id,
  });
  const [getterFees, getterFeesResult] = useFeesMutation();

  const [billFormVisible, setBillFormVisible] = useState(false);
  const [currentBillEdit, setCurrentBillEdit] = useState();

  const handleEditMarketingTag = (tagMarketing: any) => {
    setCurrentBillEdit(tagMarketing);
    setOpenDialogCreate(true);
  };

  const functionOpenDialogCreate = () => {
    setOpenDialogCreate(!openDialogCreate);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (id) {
      getterFees({
        branchId: id,
      });
    }
  }, [id]);

  console.log("currentBillEdit", currentBillEdit);

  const handleDeleteMarketingTag = async (tagId: number) => {
    try {
      await deleteMarketingTag({
        branchId: id,
        id: tagId,
      }).unwrap();
      toasts.success("Exito", "La etiqueta se ha eliminado correctamente");
      marketingTagsQuery.refetch();
    } catch (error) {
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar la etiqueta"
      );
    }
  };

  return (
    <Page
      title={t("brand.views.user_tags_business_agreements")}
      description={t("contexts.marketing_tags.subtitle")}
    >
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <TableListTagsMarketing
          OnclickTop={functionOpenDialogCreate}
          onEdit={handleEditMarketingTag}
          onDelete={(tag) => handleDeleteMarketingTag(tag.id)}
          isLoading={marketingTagsQuery.isLoading}
          tagsMarketing={marketingTagsQuery.data ?? []}
        />
        {marketingTagsQuery.data && (
          <PaginationUI
            currentPage={page}
            totalPages={Math.ceil(
              (marketingTagsQuery?.data.length ?? 0) / pageSize
            )}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            totalResults={marketingTagsQuery?.data.length ?? 0}
          />
        )}

        <Formik
          enableReinitialize
          initialValues={
            currentBillEdit
              ? {
                  ...currentBillEdit,
                  fees: currentBillEdit?.fees,
                  fees_course: currentBillEdit?.feesCourse,
                  business_name: currentBillEdit.businessName,
                  tradename: currentBillEdit.tradename,
                  tax_identification: currentBillEdit.taxIdentification,
                  code_postal: currentBillEdit.codePostal,
                  address_number: currentBillEdit.addressNumber,
                  address_floor: currentBillEdit.addressFloor,
                  location: currentBillEdit.location,
                  city: currentBillEdit.city,
                  province: currentBillEdit.province,
                  responsible: currentBillEdit.responsible,
                  email: currentBillEdit.email,
                  financial_email: currentBillEdit.financialEmail,
                }
              : billInitialValues
          }
          validationSchema={billValidationSchema}
          onSubmit={(values, formikHelpers) => {
            if (currentBillEdit) {
              updateMarketingTag({
                ...values,
                branchId: id,
                fees: values.fees.split(",").map((id) => parseInt(id)),
                fees_course: values.fees_course
                  .split(",")
                  .map((id) => parseInt(id)),
                id: currentBillEdit.id,
              });
            } else {
              createMarketingTag({
                ...values,
                branchId: id,
                fees: values.fees.split(",").map((id) => parseInt(id)),
                fees_course: values.fees_course
                  .split(",")
                  .map((id) => parseInt(id)),
              });
            }
            formikHelpers.resetForm();
            setBillFormVisible(false);
            setCurrentBillEdit(undefined);
            functionOpenDialogCreate();
          }}
        >
          {({
            handleChange,
            handleSubmit,
            errors,
            values,
            touched,
            setFieldValue,
            resetForm,
          }) => (
            <DialogCreate
              open={openDialogCreate}
              setOpen={functionOpenDialogCreate}
              tittle={t("brand.views.create_user_tag_agreements")}
              OnclickCreate={handleSubmit}
              OnclickCancel={() => {
                resetForm();
                setBillFormVisible(false);
                functionOpenDialogCreate();
              }}
              isLoading={
                createMarketingTagResult.isLoading ||
                updateMarketingTagResult.isLoading
              }
              acceptDisabled={
                createMarketingTagResult.isLoading ||
                updateMarketingTagResult.isLoading
              }
              onClose={() => {
                resetForm();
                setBillFormVisible(false);
                setCurrentBillEdit(undefined);
                functionOpenDialogCreate();
              }}
              tittleButtonCreate={
                currentBillEdit ? t("general.edit") : t("general.create")
              }
              tittleButtonCancel={t("general.cancel")}
            >
              <form onSubmit={handleSubmit}>
                <div className="mt-6 mx-2">
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("brand.views.caracteristics_tag")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.tag_name")}
                            type="text"
                            onChange={handleChange}
                            name="name"
                            placeholder={t("brand.views.tag_name")}
                            error={!!errors.name}
                            textError={errors.name}
                            value={values.name}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.discount")}
                            name="discount"
                            type="text"
                            placeholder={t("brand.views.discount")}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*$/.test(value)) {
                                handleChange(e);
                              }
                            }}
                            error={!!errors.discount}
                            textError={errors.discount}
                            value={values.discount}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            onChange={handleChange}
                            name="color"
                            label={t("brand.views.color")}
                            type="color"
                            error={!!errors.color}
                            textError={errors.color}
                            value={values.color}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.business_name")}
                            name="business_name"
                            type="text"
                            placeholder={t("brand.views.business_name")}
                            onChange={handleChange}
                            error={!!errors.business_name}
                            textError={errors.business_name}
                            value={values.business_name}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.trade_name")}
                            name="tradename"
                            id="tradename"
                            type="text"
                            placeholder={t("brand.views.trade_name")}
                            onChange={handleChange}
                            error={!!errors.tradename}
                            textError={errors.tradename}
                            value={values.tradename}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.tax_id")}
                            name="tax_identification"
                            type="text"
                            placeholder={t("brand.views.tax_id")}
                            onChange={handleChange}
                            error={!!errors.tax_identification}
                            textError={errors.tax_identification}
                            value={values.tax_identification}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.postal_code")}
                            name="code_postal"
                            type="text"
                            placeholder={t("brand.views.postal_code")}
                            onChange={handleChange}
                            error={!!errors.code_postal}
                            textError={errors.code_postal}
                            value={values.code_postal}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.address")}
                            name="address"
                            type="text"
                            placeholder={t("brand.views.address")}
                            onChange={handleChange}
                            error={!!errors.address}
                            textError={errors.address}
                            value={values.address}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.number")}
                            name="address_number"
                            type="text"
                            placeholder={t("brand.views.number")}
                            onChange={handleChange}
                            error={!!errors.address_number}
                            textError={errors.address_number}
                            value={values.address_number}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.floor_apartment")}
                            name="address_floor"
                            type="text"
                            placeholder={t("brand.views.floor_apartment")}
                            onChange={handleChange}
                            error={!!errors.address_floor}
                            textError={errors.address_floor}
                            value={values.address_floor}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.district_locality")}
                            name="location"
                            type="text"
                            placeholder={t("brand.views.district_locality")}
                            onChange={handleChange}
                            error={!!errors.location}
                            textError={errors.location}
                            value={values.location}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.city")}
                            name="city"
                            type="text"
                            placeholder={t("brand.views.city")}
                            onChange={handleChange}
                            error={!!errors.city}
                            textError={errors.city}
                            value={values.city}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.province")}
                            name="province"
                            type="text"
                            placeholder={t("brand.views.province")}
                            onChange={handleChange}
                            error={!!errors.province}
                            textError={errors.province}
                            value={values.province}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.responsible")}
                            name="responsible"
                            type="text"
                            placeholder={t("brand.views.responsible")}
                            onChange={handleChange}
                            error={!!errors.responsible}
                            textError={errors.responsible}
                            value={values.responsible}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.email")}
                            name="email"
                            type="email"
                            placeholder={t("brand.views.email")}
                            onChange={handleChange}
                            error={!!errors.email}
                            textError={errors.email}
                            value={values.email}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.financial_email")}
                            name="financial_email"
                            type="email"
                            placeholder={t("brand.views.financial_email")}
                            onChange={handleChange}
                            error={!!errors.financial_email}
                            textError={errors.financial_email}
                            value={values.financial_email}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("brand.views.discounts")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <MultiSelect
                            span={t("general.mandatory")}
                            selectedOptions={coursesFeeQuery?.data?.filter(
                              (fee) =>
                                typeof values.fees_course === "string" &&
                                values.fees_course
                                  .split(",")
                                  .includes(fee.id + "")
                            )}
                            setSelectedOptions={(selected) =>
                              setFieldValue(
                                "fees_course",
                                Array.isArray(selected)
                                  ? selected.map((fee) => fee.id).join(",")
                                  : selected.id
                              )
                            }
                            options={coursesFeeQuery?.data}
                            title={t("brand.views.fees")}
                            textDescribe={null}
                            multiple
                          />
                          {errors.fees_course && touched.fees_course && (
                            <span className="text-red-500 text-sm">
                              {errors.fees_course as string}
                            </span>
                          )}
                        </div>
                        <div className="col-span-full">
                          <MultiSelect
                            span={t("general.mandatory")}
                            selectedOptions={getterFeesResult?.data?.filter(
                              (fee) =>
                                values?.fees?.split(",")?.includes(fee.id + "")
                            )}
                            setSelectedOptions={(selected: any) => {
                              setFieldValue(
                                "fees",
                                selected?.map((fee) => fee.id).join(",")
                              );
                            }}
                            options={getterFeesResult?.data ?? []}
                            title={t("brand.views.course_fees")}
                            textDescribe=""
                            multiple
                          />
                          {errors.fees && touched.fees && (
                            <span className="text-red-500 text-sm">
                              {errors.fees as string}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogCreate>
          )}
        </Formik>
      </div>
    </Page>
  );
};
